/** Session participants admin page — Phase 2B-dsr-3-ui.
 *
 * Lists the participants on a session with per-row DSR Export +
 * Erase actions. Gated on `session.read` for the list itself; the
 * DSR actions are separately gated on `dsr.execute` (via
 * `ParticipantDsrActions`'s `PermissionGate`).
 *
 * Field users typically don't have a User row, so there's no entry
 * in the org-wide member admin for them. This page is the per-
 * session counterpart that surfaces them for compliance actions. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import PhoneInput from "@/components/PhoneInput";
import { ApiPaths, api } from "@/lib/api";
import { displayPhone } from "@/lib/phone";
import type { Session } from "@/lib/types";
import {
  ParticipantDsrActions,
  fetchSessionParticipants,
  type SessionParticipant,
} from "@/modules/dsr";


export default function SessionParticipantsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  return (
    <AppLayout
      title={t("ui.sessionParticipantsPage.title")}
      description={id ? t("ui.sessionParticipantsPage.description", { id }) : undefined}
      headerAction={
        <BackLink fallback={`/sessions/${id}`} className="btn-ghost text-sm">
          {t("ui.sessionParticipantsPage.backToSession")}
        </BackLink>
      }
    >
      <PermissionGate
        perm="session.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            <Trans
              i18nKey="ui.sessionParticipantsPage.permissionFallback"
              components={{ code: <code /> }}
            />
          </p>
        }
      >
        {id && <ParticipantsList sessionId={id} />}
      </PermissionGate>
    </AppLayout>
  );
}


function ParticipantsList({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["session-participants", sessionId, "with-pending"],
    queryFn: () => fetchSessionParticipants(sessionId, { includePending: true }),
  });
  // Pull session status so we can hide the Resend/Edit/Cancel actions
  // once the session is closed or expired — re-issuing an invite on a
  // terminated session is never the right thing to do.
  const session = useQuery({
    queryKey: ["session", sessionId],
    queryFn: async () => (await api.get<Session>(ApiPaths.session(sessionId))).data,
  });
  const terminal =
    session.data?.status === "closed" ||
    session.data?.status === "expired" ||
    session.data?.status === "cancelled";

  if (q.isLoading) {
    return <p className="text-sm text-nb-text-2">{t("ui.sessionParticipantsPage.loading")}</p>;
  }
  if (q.isError) {
    return (
      <p className="card text-sm text-cvp-danger">
        {t("ui.sessionParticipantsPage.loadError", { message: (q.error as Error).message })}
      </p>
    );
  }
  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="card text-sm text-nb-text-2">
        {t("ui.sessionParticipantsPage.empty")}
      </p>
    );
  }
  return (
    <section className="card">
      <ul className="divide-y divide-nb-border">
        {rows.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 py-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="pill bg-nb-mint/20 text-nb-mint text-xs">
                  {p.role}
                </span>
                <span
                  className={`truncate font-medium ${p.erased ? "italic text-nb-text-3" : "text-nb-text"}`}
                  title={p.display_name || p.id}
                >
                  {p.display_name || `participant:${p.id.slice(0, 8)}`}
                </span>
                {p.pending && (
                  <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                    {t("ui.sessionParticipantsPage.invitedNotJoined")}
                  </span>
                )}
                {p.erased && (
                  <span className="text-xs text-nb-text-3">{t("ui.sessionParticipantsPage.tombstoned")}</span>
                )}
              </div>
              <div className="text-xs text-nb-text-3">
                {p.pending ? (
                  <>
                    {t("ui.sessionParticipantsPage.inviteSent")} · {formatDateTime(p.created_at)}
                    {p.recipient_email && (
                      <> · {p.recipient_email}</>
                    )}
                    {!p.recipient_email && p.recipient_phone && (
                      <> · {displayPhone(p.recipient_phone)}</>
                    )}
                  </>
                ) : (
                  <>
                    {t("ui.sessionParticipantsPage.joined")}{" "}
                    {p.joined_at
                      ? formatDateTime(p.joined_at)
                      : t("ui.sessionParticipantsPage.never")}
                    {p.left_at && (
                      <>
                        {" "}· {t("ui.sessionParticipantsPage.left")} {formatDateTime(p.left_at)}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            {/* Pending invites get inline manage actions; redeemed
                rows get the DSR controls. The pending case lets the
                operator resend, edit recipient info, or cancel the
                invite without re-issuing a fresh one — but ONLY while
                the session is still live. Terminal sessions surface
                pending invites as read-only history. */}
            {p.pending ? (
              terminal ? (
                <span className="text-xs text-nb-text-3 italic">
                  {t("ui.sessionParticipantsPage.sessionEnded")}
                </span>
              ) : (
                <PendingInviteActions sessionId={sessionId} invite={p} />
              )
            ) : (
              <ParticipantDsrActions participant={p} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}


function PendingInviteActions({
  sessionId,
  invite,
}: {
  sessionId: string;
  invite: SessionParticipant;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(invite.recipient_first_name ?? "");
  const [last, setLast] = useState(invite.recipient_last_name ?? "");
  const [email, setEmail] = useState(invite.recipient_email ?? "");
  const [phone, setPhone] = useState(invite.recipient_phone ?? "");
  const [feedback, setFeedback] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["session-participants", sessionId, "with-pending"],
    });

  const resend = useMutation({
    mutationFn: () =>
      api.post<{ channels: string[] }>(
        `/v1/sessions/${sessionId}/invites/${invite.id}/resend`,
      ),
    onSuccess: (res) => {
      setFeedback(t("ui.sessionParticipantsPage.resentVia", { channels: res.data.channels.join(" + ") }));
    },
    onError: (e) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("ui.sessionParticipantsPage.resendError");
      setFeedback(detail);
    },
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.delete(`/v1/sessions/${sessionId}/invites/${invite.id}`),
    onSuccess: () => {
      void invalidate();
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/v1/sessions/${sessionId}/invites/${invite.id}`, {
        recipient_first_name: first,
        recipient_last_name: last,
        recipient_email: email,
        recipient_phone: phone,
      }),
    onSuccess: () => {
      setEditing(false);
      setFeedback(t("ui.sessionParticipantsPage.saved"));
      void invalidate();
    },
    onError: (e) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("ui.sessionParticipantsPage.saveError");
      setFeedback(detail);
    },
  });

  if (editing) {
    return (
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            className="input w-24"
            placeholder={t("ui.sessionParticipantsPage.firstPlaceholder")}
            value={first}
            onChange={(e) => setFirst(e.target.value)}
          />
          <input
            className="input w-24"
            placeholder={t("ui.sessionParticipantsPage.lastPlaceholder")}
            value={last}
            onChange={(e) => setLast(e.target.value)}
          />
          <input
            className="input w-44"
            type="email"
            placeholder={t("ui.sessionParticipantsPage.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <PhoneInput
            placeholder={t("ui.sessionParticipantsPage.phonePlaceholder")}
            value={phone}
            onChange={setPhone}
          />
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setEditing(false)}
            disabled={save.isPending}
          >
            {t("ui.sessionParticipantsPage.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? t("ui.sessionParticipantsPage.saving") : t("ui.sessionParticipantsPage.save")}
          </button>
        </div>
        {feedback && (
          <p className="text-right text-[10px] text-nb-text-3">{feedback}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1 text-xs">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
          title={t("ui.sessionParticipantsPage.resendTitle")}
        >
          {resend.isPending ? t("ui.sessionParticipantsPage.resending") : t("ui.sessionParticipantsPage.resend")}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => {
            setFirst(invite.recipient_first_name ?? "");
            setLast(invite.recipient_last_name ?? "");
            setEmail(invite.recipient_email ?? "");
            setPhone(invite.recipient_phone ?? "");
            setEditing(true);
          }}
          title={t("ui.sessionParticipantsPage.editTitle")}
        >
          {t("ui.sessionParticipantsPage.edit")}
        </button>
        <button
          type="button"
          className="btn-ghost text-xs text-cvp-danger hover:bg-cvp-danger/10"
          onClick={() => {
            if (confirm(t("ui.sessionParticipantsPage.cancelConfirm"))) {
              cancel.mutate();
            }
          }}
          disabled={cancel.isPending}
          title={t("ui.sessionParticipantsPage.cancelTitle")}
        >
          {cancel.isPending ? t("ui.sessionParticipantsPage.cancelling") : t("ui.sessionParticipantsPage.cancel")}
        </button>
      </div>
      {feedback && (
        <p className="text-[10px] text-nb-text-3">{feedback}</p>
      )}
    </div>
  );
}
